import React, {
  createRef,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useScroll } from "framer-motion";

interface IProgressContext {
  setProgress: (progress: Partial<IProgressInfo>) => any;
  lastRunRef: React.RefObject<number | null>;
}
export const ProgressContext = React.createContext<IProgressContext>({
  setProgress: () => {},
  lastRunRef: createRef<number>(),
});

export const useLastRunRef = () => React.useContext(ProgressContext).lastRunRef;

export function useProgressReporterScrollListener(
  scrollElement: RefObject<HTMLDivElement>,
) {
  const setProgress = useContext(ProgressContext).setProgress;

  const { scrollYProgress } = useScroll({ container: scrollElement });

  useEffect(() => {
    if (!scrollYProgress) {
      return;
    }
    scrollYProgress.on("change", (p) => {
      if (p < 0.01) {
        p = 0;
      } else if (p > 0.99) {
        p = 1;
      }
      setProgress({ percent: p });
    });
    return () => {
      scrollYProgress.clearListeners();
    };
  }, [scrollYProgress]);

  useEffect(() => {
    if (!scrollElement.current) {
      return;
    }

    const scrollListener = (e: Event) => {
      if (!scrollElement.current) {
        return;
      }
      let topDataIndex = 0;
      const childrens =
        // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM attribute selector
        scrollElement.current?.querySelectorAll("[data-index]") || [];
      // There could be a sticky item (the video/audio player) which needs to be ignored in the computation
      const offsetFromTopOfScreen = 0;
      for (let i = 0; i < childrens.length; i++) {
        const child = childrens[i];

        const childBottom = child.getBoundingClientRect().bottom;
        if (childBottom >= offsetFromTopOfScreen) {
          //This element's bottom is at the top of the viewport, that's the child to report on
          topDataIndex = parseInt(child.getAttribute("data-index") || "0") || 0;
          // You can break the loop once you find the first visible element
          break;
        }
      }

      // We don't want to do the rest, but we need to save the topDataIndex
      setProgress({ topDataIndex });
      return;
    };

    scrollElement.current.addEventListener("scroll", scrollListener);

    return () => {
      scrollElement.current?.removeEventListener("scroll", scrollListener);
    };
  }, [scrollElement.current]);
}

interface IProgressInfo {
  topDataIndex: number;
  percent: number;
  videoTimestamp?: number;
}
export function useProgressReporter(
  resourceID?: string,
): [IProgressContext, number] {
  const lastRunRef = useRef<number>(Date.now());
  const progressInfo = useRef<IProgressInfo>({
    topDataIndex: 0,
    percent: 0,
  });
  const [progressPercent, setProgressPercent] = useState(0);

  const recordUserProgress = (seconds: Number) => {
    // With keepalive set to true, the request will fire on reload/tab close / browser close
    // See: https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters
    fetch(`/internal/reading-progress`, {
      keepalive: true,
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        id: resourceID,
        seconds: seconds,
        lastDataIndex: progressInfo.current.topDataIndex ?? 0,
        percent: progressInfo.current.percent,
        videoTimestamp: progressInfo.current.videoTimestamp,
      }),
    });
  };

  useEffect(() => {
    if (!resourceID) {
      return;
    }

    progressInfo.current = {
      topDataIndex: 0,
      percent: 0,
    };
    // On component mount, record open time and add close listener.
    let seconds = 5;
    lastRunRef.current = Date.now();

    let interval: NodeJS.Timeout;
    interval = setInterval(() => {
      lastRunRef.current = Date.now();
      recordUserProgress(seconds);
    }, seconds * 1000);

    const pageHide = () => {
      const toSend = Math.min(
        Math.ceil((Date.now() - lastRunRef.current) / 1000),
        seconds,
      );
      recordUserProgress(toSend);
      if (interval) clearInterval(interval);
    };

    function handleVisibilityChange() {
      if (document.hidden) {
        pageHide();
      } else {
        interval = setInterval(() => {
          lastRunRef.current = Date.now();
          recordUserProgress(seconds);
        }, seconds * 1000);
      }
    }

    if (typeof document.hidden !== "undefined") {
      document.addEventListener(
        "visibilitychange",
        handleVisibilityChange,
        false,
      );
    } else {
      console.error("Page Visibility Not Supported.");
    }

    return () => {
      if (typeof document.hidden !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
          false,
        );
      }

      if (interval) clearInterval(interval);
      // If user moves to a different page WITHIN the app, record progress.
      recordUserProgress(Math.ceil((Date.now() - lastRunRef.current) / 1000));
    };
  }, [resourceID]);

  const contextValue = useMemo(
    () => ({
      setProgress: (progress: Partial<IProgressInfo>) => {
        if (progress.percent !== undefined) {
          setProgressPercent(progress.percent);
        }
        progressInfo.current = { ...progressInfo.current, ...progress };
      },
      lastRunRef,
    }),
    [resourceID],
  );
  return [contextValue, progressPercent]; // TODO: FLorian, this is bad, we need to extract this in a different way to avoid re-rendering too much.
}
